import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { batch } from "react-redux";
import { AppThunk } from "@store";
import {
  BatteriesClient,
  CreateBatteryCommand,
  IBatteriesClient,
  UpdateBatteryCommand,
  VariableDto,
} from "app/ApiClient";
import { checkAndThrowInstructionValidationException, LoadingState } from "types";
import { setBatteries } from "../batteries/batteriesSlice";
import { updateProjectSettings } from "../project/projectSlice";
import { loadProjectDictionaryData, setPageContext } from "../projectsData/projectsDataSlice";
import { initialBatteryState } from "./batteryState";

const UPDATE_ERROR_MESSAGE = "Update Error";
const CREATE_ERROR_MESSAGE = "Create Error";

const batteriesClient: IBatteriesClient = new BatteriesClient(process.env.REACT_APP_API_URL);

export const createBattery = createAsyncThunk(
  "battery/create",
  async (data: CreateBatteryCommand, { rejectWithValue }) => {
    try {
      const result = await batteriesClient.create(data);
      checkAndThrowInstructionValidationException(result);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateBattery = createAsyncThunk(
  "battery/update",
  async (data: UpdateBatteryCommand, { rejectWithValue }) => {
    try {
      const result = await batteriesClient.update(data);
      checkAndThrowInstructionValidationException(result);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const batterySlice = createSlice({
  name: "battery",
  initialState: initialBatteryState,
  reducers: {
    resetBattery: () => initialBatteryState,
    setError: (state, { payload }: PayloadAction<Nullable<{ value: string; message: string }>>) => {
      state.error = payload;
    },
  },
  extraReducers: (builder) => {
    // createBattery
    builder.addCase(createBattery.pending, (state) => {
      state.battery = undefined;
      state.loading = LoadingState.pending;
    });
    builder.addCase(createBattery.fulfilled, (state, { payload }) => {
      state.battery = payload || undefined;
      state.loading = LoadingState.succeed;
    });
    builder.addCase(createBattery.rejected, (state, payload) => {
      state.battery = undefined;
      state.loading = LoadingState.failed;
      state.error = { value: payload.meta.arg.newValue ?? "", message: payload.error.message || CREATE_ERROR_MESSAGE };
    });
    // updateBattery
    builder.addCase(updateBattery.pending, (state) => {
      state.battery = undefined;
      state.loading = LoadingState.pending;
    });
    builder.addCase(updateBattery.fulfilled, (state, { payload }) => {
      state.battery = payload;
      state.loading = LoadingState.succeed;
    });
    builder.addCase(updateBattery.rejected, (state, payload) => {
      state.battery = undefined;
      state.loading = LoadingState.failed;
      state.error = { value: payload.meta.arg.newValue ?? "", message: payload.error.message || UPDATE_ERROR_MESSAGE };
    });
  },
});
export const { resetBattery, setError } = batterySlice.actions;

const isUpdateBatteryCommand = (v: UpdateBatteryCommand | CreateBatteryCommand): v is UpdateBatteryCommand =>
  v.hasOwnProperty("id");

const findAndReplaceInArray = (array: string[], find: string, replace: string) => {
  const newArray = [...array];
  const index = newArray.indexOf(find);

  if (index !== -1) {
    newArray[index] = replace;
    return newArray;
  }

  return array;
};

type SubmitBatteryType = (v: {
  request: UpdateBatteryCommand | CreateBatteryCommand;
  variable: VariableDto;
  encodedVersionId: string;
}) => AppThunk;

export const submitBattery: SubmitBatteryType =
  ({ request, variable, encodedVersionId}) =>
  async (dispatch, getState) => {
    const { data, batteryFilterTypes } = getState().batteries;
    const { currentProject, projectSettings } = getState().project;
    const { id: projectId } = currentProject ?? {};
    const { pageContext } = getState().projectsData;
    const newBatteryValue = request.newValue;
    const previousBatteryValue = variable.battery;

    const context = {
      ...pageContext,
      filter: {
        batteries: findAndReplaceInArray(
          pageContext?.filter?.batteries || [],
          previousBatteryValue || "",
          newBatteryValue,
        ),
      },
    };

    const updateStoreData = () => {
      batch(() => {
        // battery local name update
        dispatch(
          setBatteries(
            data.map((i) =>
              i.name === previousBatteryValue
                ? {
                    ...i,
                    name: newBatteryValue,
                  }
                : i,
            ),
          ),
        );
        dispatch(setPageContext(context));

        dispatch(
          loadProjectDictionaryData({
            formDataQuery: context,
            encodedVersionId,
            modificationId: request.modificationId,
          }),
        );

        // update setting data
        if (projectId) {
          dispatch(
            updateProjectSettings({
              projectId,
              encodedVersionId,
              batteryFilterTypes,
              batteries: context.filter.batteries,
              excludedVariables:projectSettings?.normalizedExcludedVariables??[],
              modificationId: request.modificationId,
            }),
          );
        }
      });
    };

    try {
      const response = await dispatch(
        isUpdateBatteryCommand(request) ? updateBattery(request) : createBattery(request),
      );

      if (response.meta.requestStatus === "fulfilled") {
        return updateStoreData();
      }
    } catch (e) {
      console.error(e);
    }
  };

export default batterySlice.reducer;
